import React from "react";
import { useTranslation } from "react-i18next";
import { ListFilter, RefreshCw, X } from "lucide-react";
import { format, parseISO } from "date-fns";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { DateRange } from "@/hooks/useDateRange";
import { CommunicationChannelKind } from "@/types/messaging-channel-config";
import {
  MessageDeliveryDirection,
  MessageDeliveryStatus,
  MESSAGE_DELIVERY_STATUSES,
} from "@/types/message-delivery";

export const ALL_FILTER = "all" as const;

export type DirectionFilter = typeof ALL_FILTER | MessageDeliveryDirection;
export type StatusFilter = typeof ALL_FILTER | MessageDeliveryStatus;
export type ChannelFilter = typeof ALL_FILTER | CommunicationChannelKind;

const CHANNEL_OPTIONS: readonly CommunicationChannelKind[] = [
  "IN_APP",
  "EMAIL",
  "SMS",
  "WHATSAPP",
];

interface DeliveryFilterDraft {
  direction: DirectionFilter;
  status: StatusFilter;
  channel: ChannelFilter;
  recipient: string;
  dateRange: DateRange;
}

interface DeliveryFiltersProps {
  direction: DirectionFilter;
  onDirectionChange: (value: DirectionFilter) => void;
  status: StatusFilter;
  onStatusChange: (value: StatusFilter) => void;
  channel: ChannelFilter;
  onChannelChange: (value: ChannelFilter) => void;
  recipient: string;
  onRecipientChange: (value: string) => void;
  dateRange: DateRange;
  onDateRangeChange: (range: DateRange) => void;
  bookingId?: string;
  onClearBooking: () => void;
  onRefresh: () => void;
  onClear: () => void;
  isFetching: boolean;
}

const isDirectionFilter = (value: string): value is DirectionFilter =>
  value === ALL_FILTER || value === "INBOUND" || value === "OUTBOUND";

const isStatusFilter = (value: string): value is StatusFilter =>
  value === ALL_FILTER ||
  MESSAGE_DELIVERY_STATUSES.some((status) => status === value);

const isChannelFilter = (value: string): value is ChannelFilter =>
  value === ALL_FILTER || CHANNEL_OPTIONS.some((channel) => channel === value);

const formatDateInputValue = (value: Date | undefined): string =>
  value ? format(value, "yyyy-MM-dd") : "";

export const DeliveryFilters: React.FC<DeliveryFiltersProps> = ({
  direction,
  onDirectionChange,
  status,
  onStatusChange,
  channel,
  onChannelChange,
  recipient,
  onRecipientChange,
  dateRange,
  onDateRangeChange,
  bookingId,
  onClearBooking,
  onRefresh,
  onClear,
  isFetching,
}) => {
  const { t } = useTranslation();
  const [open, setOpen] = React.useState(false);
  const [draft, setDraft] = React.useState<DeliveryFilterDraft>({
    direction,
    status,
    channel,
    recipient,
    dateRange,
  });
  const activeFilterCount = [
    direction !== ALL_FILTER,
    status !== ALL_FILTER,
    channel !== ALL_FILTER,
    recipient.trim().length > 0,
    Boolean(dateRange.from || dateRange.to),
    Boolean(bookingId),
  ].filter(Boolean).length;

  const handleOpenChange = (nextOpen: boolean): void => {
    setOpen(nextOpen);
    if (nextOpen) {
      setDraft({ direction, status, channel, recipient, dateRange });
    }
  };

  const handleApply = (): void => {
    onDirectionChange(draft.direction);
    onStatusChange(draft.status);
    onChannelChange(draft.channel);
    onRecipientChange(draft.recipient);
    onDateRangeChange(draft.dateRange);
    setOpen(false);
  };

  const handleReset = (): void => {
    setDraft({
      direction: ALL_FILTER,
      status: ALL_FILTER,
      channel: ALL_FILTER,
      recipient: "",
      dateRange: {},
    });
    onClear();
  };

  return (
    <section
      aria-label={t("communicationChannels.deliveries.filters.regionAria")}
      className="border-base-border flex flex-wrap items-center justify-between gap-3 rounded-xl border bg-white p-4"
    >
      <div className="flex flex-wrap items-center gap-2">
        <Popover open={open} onOpenChange={handleOpenChange}>
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="icon-text"
              className="text-foreground border-base-border h-8 min-h-8 w-fit gap-2 border py-1 text-sm font-medium shadow-xs"
            >
              <ListFilter />
              {t("communicationChannels.deliveries.filters.title")}
              {activeFilterCount > 0 ? (
                <Badge variant="number_secondary">{activeFilterCount}</Badge>
              ) : null}
            </Button>
          </PopoverTrigger>

          <PopoverContent
            align="start"
            sideOffset={8}
            className="border-base-border flex max-h-[min(620px,calc(100vh-120px))] w-[360px] max-w-[calc(100vw-2rem)] flex-col gap-5 overflow-hidden rounded-2xl border bg-white p-4 shadow-md"
          >
            <div className="flex shrink-0 items-start justify-between gap-2">
              <h4 className="text-base leading-normal font-semibold text-black/96">
                {t("communicationChannels.deliveries.filters.title")}
              </h4>
              <Button
                type="button"
                variant="span"
                className="hover:bg-secondary-badge rounded p-1 text-black/45 transition hover:text-black/96"
                onClick={() => setOpen(false)}
                aria-label={t("communicationChannels.deliveries.filters.close")}
              >
                <X className="size-5" />
              </Button>
            </div>

            <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto pr-0.5">
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="delivery-direction">
                  {t("communicationChannels.deliveries.columns.direction")}
                </Label>
                <Select
                  value={draft.direction}
                  onValueChange={(value) => {
                    if (isDirectionFilter(value)) {
                      setDraft((previous) => ({
                        ...previous,
                        direction: value,
                      }));
                    }
                  }}
                >
                  <SelectTrigger
                    id="delivery-direction"
                    className="w-full justify-between"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      <SelectItem value={ALL_FILTER}>
                        {t("communicationChannels.deliveries.filters.all")}
                      </SelectItem>
                      <SelectItem value="INBOUND">
                        {t(
                          "communicationChannels.deliveries.direction.INBOUND",
                        )}
                      </SelectItem>
                      <SelectItem value="OUTBOUND">
                        {t(
                          "communicationChannels.deliveries.direction.OUTBOUND",
                        )}
                      </SelectItem>
                    </SelectGroup>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex flex-col gap-1.5">
                <Label htmlFor="delivery-status">
                  {t("communicationChannels.deliveries.columns.status")}
                </Label>
                <Select
                  value={draft.status}
                  onValueChange={(value) => {
                    if (isStatusFilter(value)) {
                      setDraft((previous) => ({
                        ...previous,
                        status: value,
                      }));
                    }
                  }}
                >
                  <SelectTrigger
                    id="delivery-status"
                    className="w-full justify-between"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      <SelectItem value={ALL_FILTER}>
                        {t("communicationChannels.deliveries.filters.all")}
                      </SelectItem>
                      {MESSAGE_DELIVERY_STATUSES.map((deliveryStatus) => (
                        <SelectItem key={deliveryStatus} value={deliveryStatus}>
                          {t(
                            `communicationChannels.deliveries.status.${deliveryStatus}`,
                          )}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex flex-col gap-1.5">
                <Label htmlFor="delivery-channel">
                  {t("communicationChannels.deliveries.filters.channel")}
                </Label>
                <Select
                  value={draft.channel}
                  onValueChange={(value) => {
                    if (isChannelFilter(value)) {
                      setDraft((previous) => ({
                        ...previous,
                        channel: value,
                      }));
                    }
                  }}
                >
                  <SelectTrigger
                    id="delivery-channel"
                    className="w-full justify-between"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      <SelectItem value={ALL_FILTER}>
                        {t(
                          "communicationChannels.deliveries.filters.channelAll",
                        )}
                      </SelectItem>
                      {CHANNEL_OPTIONS.map((deliveryChannel) => (
                        <SelectItem
                          key={deliveryChannel}
                          value={deliveryChannel}
                        >
                          {t(
                            `communicationChannels.composer.badge.${deliveryChannel}`,
                          )}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  </SelectContent>
                </Select>
              </div>

              <Input
                id="delivery-recipient"
                label={t("communicationChannels.deliveries.filters.recipient")}
                value={draft.recipient}
                onChange={(event) =>
                  setDraft((previous) => ({
                    ...previous,
                    recipient: event.target.value,
                  }))
                }
                placeholder={t(
                  "communicationChannels.deliveries.filters.recipientPlaceholder",
                )}
              />

              <div className="flex flex-col gap-3">
                <Label>
                  {t("communicationChannels.deliveries.filters.dateRange")}
                </Label>
                <div className="grid grid-cols-2 gap-2">
                  <Input
                    type="date"
                    label={t("common.dateRange.fromDate")}
                    value={formatDateInputValue(draft.dateRange.from)}
                    max={formatDateInputValue(draft.dateRange.to)}
                    onChange={(event) =>
                      setDraft((previous) => ({
                        ...previous,
                        dateRange: {
                          ...previous.dateRange,
                          from: event.target.value
                            ? parseISO(event.target.value)
                            : undefined,
                        },
                      }))
                    }
                  />
                  <Input
                    type="date"
                    label={t("common.dateRange.toDate")}
                    value={formatDateInputValue(draft.dateRange.to)}
                    min={formatDateInputValue(draft.dateRange.from)}
                    onChange={(event) =>
                      setDraft((previous) => ({
                        ...previous,
                        dateRange: {
                          ...previous.dateRange,
                          to: event.target.value
                            ? parseISO(event.target.value)
                            : undefined,
                        },
                      }))
                    }
                  />
                </div>
              </div>
            </div>

            <div className="flex shrink-0 items-center justify-end gap-2">
              <Button type="button" variant="new_outline" onClick={handleReset}>
                {t("communicationChannels.deliveries.filters.reset")}
              </Button>
              <Button type="button" variant="new_default" onClick={handleApply}>
                {t("communicationChannels.deliveries.filters.apply")}
              </Button>
            </div>
          </PopoverContent>
        </Popover>

        {bookingId ? (
          <Badge variant="new_secondary" className="gap-1">
            {t("communicationChannels.deliveries.filters.bookingChip")}:{" "}
            {bookingId}
            <button
              type="button"
              onClick={onClearBooking}
              aria-label={t(
                "communicationChannels.deliveries.filters.clearBookingAria",
              )}
              className="ml-1 inline-flex"
            >
              <X className="size-3" aria-hidden />
            </button>
          </Badge>
        ) : null}
      </div>

      <Button
        type="button"
        variant="new_outline"
        onClick={onRefresh}
        disabled={isFetching}
      >
        <RefreshCw className={isFetching ? "animate-spin" : undefined} />
        {t("communicationChannels.deliveries.filters.refresh")}
      </Button>
    </section>
  );
};
